/**
 * Tarjeta de entrada a un módulo. Compartida por el menú principal (Hub) y la
 * pantalla "Datos de empresa" para mantener el mismo aspecto y comportamiento
 * (estado bloqueado, tooltip y efecto de hover).
 */
import { NavLink } from "react-router-dom";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";

export type EntryCardProps = {
  to?: string;
  /** Acción alternativa cuando la tarjeta no navega directamente. */
  onClick?: () => void;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  disabled?: boolean;
  disabledHint?: string;
};

const EntryCard = ({ to, onClick, icon: Icon, title, description, disabled, disabledHint }: EntryCardProps) => {
  const base =
    "group relative flex flex-col items-start gap-4 rounded-2xl border bg-card p-6 text-left transition-all min-h-[190px]";
  const enabledCls =
    "hover:border-brand hover:shadow-lg hover:shadow-brand/10 hover:-translate-y-0.5 hover:bg-brand/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";
  const disabledCls = "opacity-60 cursor-not-allowed";

  const content = (
    <>
      <div
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-xl transition-colors",
          disabled
            ? "bg-muted text-muted-foreground"
            : "bg-brand/10 text-brand group-hover:bg-brand group-hover:text-brand-foreground",
        )}
      >
        <Icon className="h-6 w-6" />
      </div>
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight font-[Playfair_Display,serif] flex items-center gap-2 group-hover:text-brand transition-colors">
          {title}
          {disabled && <Lock className="h-4 w-4 text-muted-foreground" />}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed group-hover:text-brand/80 transition-colors">
          {disabled ? disabledHint ?? description : description}
        </p>
      </div>
      {!disabled && (
        <span className="mt-auto text-xs font-medium text-brand opacity-0 group-hover:opacity-100 transition-opacity">
          Entrar →
        </span>
      )}
    </>
  );

  if (disabled || (!to && !onClick)) {
    return (
      <div className={cn(base, disabledCls)} aria-disabled title={disabledHint}>
        {content}
      </div>
    );
  }

  if (!to && onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(base, enabledCls, "w-full")}>
        {content}
      </button>
    );
  }

  return (
    <NavLink to={to!} className={cn(base, enabledCls)}>
      {content}
    </NavLink>
  );
};

export default EntryCard;
